/**
 * Partner Constants
 *
 * Centralized configuration for all partner-related constants and utilities.
 * This file serves as the single source of truth for partner identifiers,
 * configurations, and helper functions.
 */

// ============================================================================
// Partner Identifiers
// ============================================================================

export const PARTNER_IDENTIFIERS = {
  BE: "be",
  VTM: "vtm",
  IZOTA: "izota",
  UTS: "uts",
  VNTR: "vntr",
  ZALO: "zalo",
  VPB: "vpb",
  SKYJOY: "skyjoy",
} as const

export type PartnerIdentifier =
  (typeof PARTNER_IDENTIFIERS)[keyof typeof PARTNER_IDENTIFIERS]

/**
 * List of all allowed partners in the system
 */
export const ALLOWED_PARTNERS = [
  PARTNER_IDENTIFIERS.BE,
  PARTNER_IDENTIFIERS.VTM,
  PARTNER_IDENTIFIERS.IZOTA,
  PARTNER_IDENTIFIERS.UTS,
  PARTNER_IDENTIFIERS.VNTR,
  PARTNER_IDENTIFIERS.VPB,
  PARTNER_IDENTIFIERS.SKYJOY,
] as const

export type AllowedPartner = (typeof ALLOWED_PARTNERS)[number]

/**
 * Extended list of allowed partners including Zalo (used in partner config validation)
 */
export const ALLOWED_PARTNERS_EXTENDED = [
  ...ALLOWED_PARTNERS,
  PARTNER_IDENTIFIERS.ZALO,
] as const

export type AllowedPartnerExtended = (typeof ALLOWED_PARTNERS_EXTENDED)[number]

/**
 * Default partner identifier when none is specified or invalid
 */
export const DEFAULT_PARTNER = PARTNER_IDENTIFIERS.VTM

/**
 * Alternate ptr values accepted on launch URL, mapped to canonical identifiers.
 */
export const PARTNER_PTR_ALIASES: Record<string, AllowedPartnerExtended> = {
  vntrip: PARTNER_IDENTIFIERS.VNTR,
  vpbank: PARTNER_IDENTIFIERS.VPB,
}

// ============================================================================
// Partner Feature Flags
// ============================================================================

/**
 * Partners that require automatic invoice creation
 * These partners have pre-configured invoice data that is automatically applied
 */
export const AUTO_INVOICE_PARTNERS = [
  PARTNER_IDENTIFIERS.UTS,
  PARTNER_IDENTIFIERS.VNTR,
] as const

export type AutoInvoicePartner = (typeof AUTO_INVOICE_PARTNERS)[number]

/**
 * Partners that are not eligible to update/modify invoice information
 * Invoice UI should be hidden for these partners
 */
export const INVOICE_DISABLED_PARTNERS = AUTO_INVOICE_PARTNERS

// ============================================================================
// Partner Data Configuration
// ============================================================================

export interface PartnerData {
  allowed_payment_method?: number[]
  source?: number
  name: string
  allow_change_payment_method?: boolean
  source_app?: string
}

export const PARTNER_DATA: Record<AllowedPartner, PartnerData> = {
  [PARTNER_IDENTIFIERS.VTM]: {
    allowed_payment_method: [29],
    name: "Viettel Money",
    allow_change_payment_method: false,
  },
  [PARTNER_IDENTIFIERS.BE]: {
    allowed_payment_method: [29],
    name: "be",
    allow_change_payment_method: false,
  },
  [PARTNER_IDENTIFIERS.IZOTA]: {
    source: 3,
    name: "Izota",
    allow_change_payment_method: true,
  },
  [PARTNER_IDENTIFIERS.UTS]: {
    source: 5,
    name: "UTS",
    allow_change_payment_method: false,
    allowed_payment_method: [39],
  },
  [PARTNER_IDENTIFIERS.VNTR]: {
    source: 6,
    name: "VNTrip",
    allow_change_payment_method: true,
  },
  [PARTNER_IDENTIFIERS.VPB]: {
    allowed_payment_method: [30],
    name: "VPBank",
    allow_change_payment_method: false,
    source_app: "vpbank",
  },
  [PARTNER_IDENTIFIERS.SKYJOY]: {
    source: 3,
    name: "SkyJoy",
    allow_change_payment_method: true,
  },
}

// ============================================================================
// Partner Invoice Data
// ============================================================================

export interface PartnerInvoiceData {
  company_name: string
  tax_code: string
  address: string
  email: string
}

export const PARTNER_INVOICE_DATA: Record<
  AutoInvoicePartner,
  PartnerInvoiceData
> = {
  [PARTNER_IDENTIFIERS.UTS]: {
    company_name: "CÔNG TY CỔ PHẦN UNIVERSAL TRAVEL SOLUTIONS NETWORK",
    tax_code: "0317855759",
    address:
      "Tầng 3, Harbour View Tower, số 35 Nguyễn Huệ, Phường Sài Gòn, Thành phố Hồ Chí Minh, Việt Nam",
    email: "utsinvoice4@gmail.com",
  },
  [PARTNER_IDENTIFIERS.VNTR]: {
    company_name: "CÔNG TY TNHH CÔNG NGHỆ VNTRIP",
    tax_code: "0109760961",
    address: "Số 10/55 Thụy Khuê, Phường Tây Hồ, Thành phố Hà Nội, Việt Nam",
    email: "",
  },
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Checks if a partner identifier is in the allowed partners list
 */
export const isPartnerAllowed = (partnerIdentifier: string | null): boolean => {
  if (!partnerIdentifier) return false
  return (ALLOWED_PARTNERS as readonly string[]).includes(partnerIdentifier)
}

/**
 * Checks if a partner identifier is in the extended allowed partners list (includes Zalo)
 */
export const isPartnerAllowedExtended = (
  partnerIdentifier: string | null
): boolean => {
  if (!partnerIdentifier) return false
  return (ALLOWED_PARTNERS_EXTENDED as readonly string[]).includes(
    partnerIdentifier
  )
}

/**
 * Checks if a partner requires automatic invoice creation
 */
export const isAutoInvoicePartner = (
  partnerIdentifier: string | null
): boolean => {
  if (!partnerIdentifier) return false
  return (AUTO_INVOICE_PARTNERS as readonly string[]).includes(
    partnerIdentifier
  )
}

/**
 * Checks if invoice UI should be hidden for a partner
 * (Same as isAutoInvoicePartner, but named for clarity in UI context)
 */
export const shouldHideInvoiceUI = (
  partnerIdentifier: string | null
): boolean => {
  return isAutoInvoicePartner(partnerIdentifier)
}

/**
 * Checks if a partner is not eligible to update invoice information
 */
export const isNotEligibleToUpdateInvoice = (
  partnerIdentifier: string | null
): boolean => {
  return isAutoInvoicePartner(partnerIdentifier)
}

/**
 * Gets partner data for a given partner identifier
 * Falls back to default partner (VTM) if not found
 */
export const getPartnerData = (
  partnerIdentifier: string | null
): PartnerData => {
  if (!partnerIdentifier || !isPartnerAllowed(partnerIdentifier)) {
    return PARTNER_DATA[DEFAULT_PARTNER]
  }
  return PARTNER_DATA[partnerIdentifier as AllowedPartner]
}

/**
 * Gets partner invoice data for auto-invoice partners
 * Returns null if partner is not an auto-invoice partner
 */
export const getPartnerInvoiceData = (
  partnerIdentifier: string | null
): PartnerInvoiceData | null => {
  if (!partnerIdentifier || !isAutoInvoicePartner(partnerIdentifier)) {
    return null
  }
  return PARTNER_INVOICE_DATA[partnerIdentifier as AutoInvoicePartner]
}

/**
 * Gets the source_app for a partner identifier
 * Falls back to canonical partner identifier if not explicitly configured in partner data
 */
export const getPartnerSourceApp = (
  partnerIdentifier: string | null
): string => {
  if (!partnerIdentifier) return DEFAULT_PARTNER
  const normalized = normalizePartnerIdentifier(partnerIdentifier)
  const partnerData = getPartnerData(normalized)
  return partnerData?.source_app ?? normalized
}

/**
 * Normalizes ptr aliases (e.g. vntrip → vntr) before validation.
 */
export const normalizePartnerIdentifier = (identifier: string): string => {
  const lowercaseIdentifier = identifier.toLowerCase()
  return PARTNER_PTR_ALIASES[lowercaseIdentifier] ?? lowercaseIdentifier
}

/**
 * Validates and normalizes a partner identifier
 * Returns the validated identifier or default partner if invalid
 */
export const validatePartnerIdentifier = (
  identifier: string | null
): AllowedPartnerExtended => {
  if (!identifier) return DEFAULT_PARTNER
  const normalizedIdentifier = normalizePartnerIdentifier(identifier)
  return isPartnerAllowedExtended(normalizedIdentifier)
    ? (normalizedIdentifier as AllowedPartnerExtended)
    : DEFAULT_PARTNER
}

/**
 * Creates invoice payload for API call for auto-invoice partners
 * @throws Error if partner is not an auto-invoice partner
 */
export const createPartnerInvoicePayload = (
  partnerIdentifier: string,
  userId: number
) => {
  const invoiceData = getPartnerInvoiceData(partnerIdentifier)

  if (!invoiceData) {
    throw new Error(
      `Invalid partner identifier for auto-invoice: ${partnerIdentifier}`
    )
  }

  return {
    invoice: {
      company_name: invoiceData.company_name,
      company_tax_no: invoiceData.tax_code,
      company_address: invoiceData.address,
      email_receiver: invoiceData.email,
      user_id: userId,
    },
  }
}
